import React, { useEffect, useMemo, useState } from "react";
import MaterialIcon from "../../../../shared/components/MaterialIcon";
import ReviewAuthorAvatar from "../../../../shared/components/ReviewAuthorAvatar";
import DeletionCaseOutcomeBadge from "../../../../shared/components/deletionCase/DeletionCaseOutcomeBadge";
import { GoogleBusinessLogo, JamedaLogo } from "../../platformCatalog";
import { useTranslation, getReplyTones, getReplyTemplate } from "../../../../i18n";
import { normalizeReviewOutcome, REVIEW_OUTCOME } from "../../../../shared/utils/deletionCaseOutcomes";

const PREVIEW_WORD_LIMIT = 13;

function formatRelativeDate(iso, t, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return t("reviewHub.justNow");
  if (hours < 24) return t("reviewHub.hoursAgo", { hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t("reviewHub.oneDayAgo");
  if (days < 7) return t("reviewHub.daysAgo", { days });
  const dateLocale = locale === "de" ? "de-DE" : "en-US";
  return d.toLocaleDateString(dateLocale, { month: "short", day: "numeric" });
}

function truncateToWords(text, maxWords = PREVIEW_WORD_LIMIT) {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return trimmed;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

const ReviewHubCard = ({
  review,
  layout = "list",
  removalSelectionEnabled = false,
  selectedForRemoval = false,
  onToggleRemovalSelection,
  replyComposerOpen,
  onToggleReply,
  onSendReply,
  sending,
  pendingReplyText,
}) => {
  const { t, locale } = useTranslation();
  const replyTones = useMemo(() => getReplyTones(t, locale), [t, locale]);

  const rating = Number(review.rating) || 0;
  const hasReply = Boolean(review.reply?.comment);
  const hasDeletionCase = Boolean(review.deletionCase);
  const deletionOutcome = hasDeletionCase
    ? normalizeReviewOutcome(review.deletionCase.deletionOutcome)
    : null;
  const showDeletionOutcomeBadge =
    hasDeletionCase && deletionOutcome !== REVIEW_OUTCOME.PENDING;
  const isPositive = rating >= 4;
  const highRisk = rating <= 2;
  const isCompact = layout === "grid";
  const text = review.text || "";
  const [tone, setTone] = useState("professional");
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const isTruncatable = text.trim().split(/\s+/).filter(Boolean).length > PREVIEW_WORD_LIMIT;
  const displayText =
    isCompact && !expanded && isTruncatable ? truncateToWords(text) : text;

  useEffect(() => {
    if (replyComposerOpen) {
      setDraft(getReplyTemplate(tone, review, t, locale));
    }
  }, [replyComposerOpen, tone, review, t, locale]);

  const borderAccent =
    removalSelectionEnabled && selectedForRemoval
      ? "border-secondary ring-2 ring-secondary/20"
      : highRisk
        ? "border-error/40"
        : "border-outline-variant/40";

  // Selection mode: the entire card toggles selection, not just the checkbox.
  // Clicks on interactive children (reply, see-more, links, inputs) are ignored.
  const cardSelectable = removalSelectionEnabled && !hasDeletionCase;
  const handleCardClick = (e) => {
    if (!cardSelectable) return;
    if (e.target.closest("button, a, textarea, input, select, label")) return;
    onToggleRemovalSelection?.();
  };

  return (
    <article
      onClick={handleCardClick}
      className={`tonal-card rounded-2xl border bg-surface-container-lowest ${borderAccent} ${
        isCompact ? "flex h-full flex-col p-4 sm:p-5" : "p-6"
      } ${cardSelectable ? "cursor-pointer" : ""}`}
    >
      <div className={`flex items-start gap-3 ${removalSelectionEnabled ? "" : "pl-0"}`}>
        {removalSelectionEnabled && !hasDeletionCase ? (
          <button
            type="button"
            onClick={onToggleRemovalSelection}
            className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-all ${
              selectedForRemoval
                ? "border-secondary bg-secondary"
                : "border-outline-variant hover:border-secondary"
            }`}
            aria-label={
              selectedForRemoval
                ? t("reviewHub.deselectForRemoval")
                : t("reviewHub.selectForRemoval")
            }
          >
            {selectedForRemoval ? (
              <MaterialIcon name="check" className="text-[14px] text-on-secondary" filled />
            ) : null}
          </button>
        ) : null}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {!isCompact ? (
              review.platform === "jameda" ? (
                <JamedaLogo className="h-8 w-8" />
              ) : (
                <GoogleBusinessLogo className="h-8 w-8" />
              )
            ) : null}
            {highRisk ? (
              <span className="rounded bg-error-container px-2 py-0.5 text-[10px] font-label-caps uppercase text-on-error-container">
                {t("reviewHub.highRisk")}
              </span>
            ) : null}
            {showDeletionOutcomeBadge ? (
              <DeletionCaseOutcomeBadge outcome={deletionOutcome} />
            ) : null}
            {!hasReply && !isPositive && !hasDeletionCase ? (
              <span className="rounded bg-surface-container-high px-2 py-0.5 text-[10px] font-label-caps uppercase text-on-surface-variant">
                {t("reviewHub.needsReply")}
              </span>
            ) : null}
            <ReviewAuthorAvatar
              author={review.author}
              photoUrl={review.authorPhoto}
              className="h-7 w-7"
              textClassName="text-[10px]"
            />
            <span className="truncate font-headline-md text-[15px] text-primary">{review.author}</span>
            <span className="inline-flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <MaterialIcon
                  key={star}
                  name="star"
                  filled={star <= rating}
                  className={`text-[14px] ${star <= rating ? "text-yellow-500" : "text-outline-variant"}`}
                />
              ))}
            </span>
            <span className="text-[11px] text-outline">
              {formatRelativeDate(review.createTime, t, locale)}
            </span>
          </div>

          {highRisk && !hasReply && !isCompact ? (
            <div className="mt-3 rounded-xl border border-error/20 bg-error-container/40 px-3 py-2">
              <p className="flex items-start gap-2 text-[11px] font-medium text-on-error-container">
                <MaterialIcon name="warning" className="shrink-0 text-[14px]" />
                {t("reviewHub.criticalHint")}
              </p>
            </div>
          ) : null}

          <blockquote
            className={`mt-3 text-body-md leading-relaxed text-on-surface-variant ${
              isCompact ? "" : "border-l-2 border-outline-variant pl-4"
            }`}
          >
            {displayText || t("reviewHub.noReviewText")}
          </blockquote>

          {isCompact && isTruncatable ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-label-sm font-medium text-secondary hover:underline"
            >
              {expanded ? t("reviewHub.seeLess") : t("reviewHub.seeMore")}
            </button>
          ) : null}

          {hasReply ? (
            <div className="mt-3 rounded-xl border border-secondary/30 bg-secondary-container/25 p-3">
              <div className="mb-1 flex items-center gap-2">
                <MaterialIcon name="reply" className="text-secondary text-[16px]" />
                <span className="text-[11px] font-label-caps text-on-secondary-container">
                  {t("reviewHub.yourResponse")}
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-on-surface">{review.reply.comment}</p>
            </div>
          ) : null}

          {pendingReplyText && !hasReply ? (
            <div className="mt-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-[12px] text-yellow-800">
              {t("reviewHub.replyQueued")}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`mt-4 flex flex-wrap items-center gap-2 border-t border-outline-variant/30 pt-4 ${
          isCompact ? "mt-auto" : ""
        }`}
      >
        {!hasReply && !pendingReplyText ? (
          <>
            {review.platform === "jameda" ? (
              <span className="inline-flex items-center gap-1 rounded-lg border border-outline-variant/50 bg-surface-container-low px-3 py-1.5 text-[12px] text-on-surface-variant">
                <MaterialIcon name="info" className="text-[14px]" />
                {t("reviewHub.jamedaReplyUnavailable")}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onToggleReply(review.id)}
                aria-expanded={replyComposerOpen}
                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  replyComposerOpen
                    ? "border border-outline-variant bg-surface-container-low text-on-surface-variant hover:bg-surface-container"
                    : "bg-primary text-on-primary"
                }`}
              >
                <MaterialIcon
                  name={replyComposerOpen ? "close" : "reply"}
                  className="text-[14px]"
                />
                {replyComposerOpen ? t("reviewHub.cancel") : t("reviewHub.reply")}
              </button>
            )}
            {removalSelectionEnabled && !isPositive && !hasDeletionCase ? (
              <button
                type="button"
                onClick={() => onToggleRemovalSelection()}
                aria-pressed={selectedForRemoval}
                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  selectedForRemoval
                    ? "border border-secondary bg-secondary-container text-on-secondary-container hover:bg-secondary-container/80"
                    : "border border-error/40 text-error hover:bg-error-container/30"
                }`}
              >
                <MaterialIcon
                  name={selectedForRemoval ? "remove_shopping_cart" : "delete"}
                  className="text-[14px]"
                />
                {selectedForRemoval
                  ? t("reviewHub.removeFromCart")
                  : t("reviewHub.requestRemoval")}
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {replyComposerOpen && !hasReply && !pendingReplyText ? (
        <div className="mt-4 rounded-xl border border-outline-variant bg-surface-container-low p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-outline-variant bg-surface-container-lowest p-1">
              {Object.entries(replyTones).map(([key, { label }]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTone(key)}
                  className={`rounded-md px-2.5 py-1 text-[12px] font-medium ${
                    tone === key ? "bg-primary text-on-primary" : "text-on-surface-variant"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-2 text-body-md outline-none focus:ring-2 focus:ring-secondary"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              disabled={sending || !draft.trim()}
              onClick={() => onSendReply(review, draft.trim())}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-[12px] font-medium text-on-primary disabled:opacity-50"
            >
              {sending ? t("reviewHub.sending") : t("reviewHub.sendResponse")}
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
};

export default ReviewHubCard;
